"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuthoringScope } from "./authoring-scope";
import {
  createLayoutPreviewLoader,
  type PreviewRenderer,
} from "./layout-preview-loader";

const PreviewContext = createContext<{
  admin: boolean;
  load: (url: string, signal: AbortSignal) => Promise<string>;
} | null>(null);

function createManager() {
  let resolve!: (api: PreviewRenderer | null) => void;
  const renderer = new Promise<PreviewRenderer | null>((ready) => {
    resolve = ready;
  });
  const manager = {
    ...createLayoutPreviewLoader(renderer),
    failed: false,
    settled: false,
    ready(api: PreviewRenderer | null) {
      if (manager.settled) return;
      manager.settled = true;
      manager.failed = !api;
      resolve(api);
    },
  };
  return manager;
}

// Share one original renderer across the list. Cards load their own protected
// documents only as they approach the viewport; no saved images can go stale.
export function LayoutPreviewProvider({ children }: { children: ReactNode }) {
  const authoring = useAuthoringScope();
  const parent = useContext(PreviewContext);
  if (parent?.admin === authoring.admin) return children;
  return (
    <PreviewProvider key={String(authoring.admin)}>{children}</PreviewProvider>
  );
}

function PreviewProvider({ children }: { children: ReactNode }) {
  const authoring = useAuthoringScope();
  const [requested, setRequested] = useState(false);
  const [frameVersion, setFrameVersion] = useState(0);
  const [rendererVersion, setRendererVersion] = useState(0);
  const frame = useRef<HTMLIFrameElement>(null);
  const manager = useRef<ReturnType<typeof createManager> | null>(null);
  const rendererReady = useCallback(() => {
    const api = (
      frame.current?.contentWindow as
        | (Window & { tamaribaLayout?: PreviewRenderer })
        | null
    )?.tamaribaLayout;
    // A background tab or slow iframe can finish after the timeout. Retry
    // current cards without requiring a layout save or route remount.
    if (api?.preview && manager.current?.failed) {
      manager.current.dispose();
      manager.current = createManager();
      setRendererVersion((version) => version + 1);
    }
    manager.current?.ready(api?.preview ? api : null);
  }, []);
  useEffect(() => {
    const ready = (event: MessageEvent) => {
      if (
        event.origin === window.location.origin &&
        event.source === frame.current?.contentWindow &&
        event.data === "tamariba-layout-preview-ready"
      )
        rendererReady();
    };
    window.addEventListener("message", ready);
    return () => window.removeEventListener("message", ready);
  }, [rendererReady]);
  // Create in the effect lifecycle so Strict Mode's setup/cleanup can restart.
  // biome-ignore lint/correctness/useExhaustiveDependencies: Late readiness retries mounted consumers through a new context value.
  const context = useMemo(
    () => ({
      admin: authoring.admin,
      load: (url: string, signal: AbortSignal) => {
        setRequested(true);
        if (manager.current?.failed) {
          manager.current.dispose();
          manager.current = null;
          setFrameVersion((version) => version + 1);
        }
        manager.current ??= createManager();
        return manager.current.load(url, signal);
      },
    }),
    [authoring.admin, rendererVersion],
  );
  useEffect(
    () => () => {
      manager.current?.ready(null);
      manager.current?.dispose();
      manager.current = null;
    },
    [],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: A retry needs its own readiness timeout.
  useEffect(() => {
    if (!requested) return;
    const timeout = setTimeout(() => manager.current?.ready(null), 15000);
    return () => clearTimeout(timeout);
  }, [requested, frameVersion]);
  return (
    <PreviewContext.Provider value={context}>
      {children}
      {requested && (
        <iframe
          key={frameVersion}
          ref={frame}
          className="layout-preview-renderer"
          title="配置図プレビュー描画"
          aria-hidden="true"
          tabIndex={-1}
          src={authoring.href("/layouts/editor/preview.html")}
          onLoad={rendererReady}
          onError={() => manager.current?.ready(null)}
        />
      )}
    </PreviewContext.Provider>
  );
}

export function LayoutPreview({
  id,
  title,
  revision,
  href,
  onEnlarge,
}: {
  id: string;
  title: string;
  revision: number;
  href?: string | null;
  onEnlarge?: (image: string, trigger: HTMLButtonElement) => void;
}) {
  const authoring = useAuthoringScope();
  const previews = useContext(PreviewContext);
  const container = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [image, setImage] = useState("");
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    const element = container.current;
    if (!element) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt explicitly retries a failed request.
  useEffect(() => {
    if (!previews || !visible) return;
    const loadPreview = previews.load;
    let cancelled = false;
    let objectUrl = "";
    const abort = new AbortController();
    setImage("");
    setError(false);
    async function load() {
      try {
        const svg = await loadPreview(
          authoring.api(`/api/layouts/${id}?revision=${revision}`),
          abort.signal,
        );
        if (cancelled) return;
        objectUrl = URL.createObjectURL(
          new Blob([svg], { type: "image/svg+xml" }),
        );
        setImage(objectUrl);
      } catch (error) {
        if (!cancelled) {
          if (process.env.NODE_ENV === "development")
            console.warn("Layout preview failed", error);
          setError(true);
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
      abort.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [previews, visible, id, revision, attempt, authoring]);
  return (
    <div ref={container} className="layout-preview">
      {image && !error && href === null && onEnlarge ? (
        <button
          type="button"
          className="layout-preview-link layout-preview-enlarge"
          aria-label={`${title}の配置図を拡大`}
          aria-haspopup="dialog"
          onClick={(event) => onEnlarge(image, event.currentTarget)}
        >
          {/* biome-ignore lint/performance/noImgElement: Reuse the authenticated SVG Blob without changing the original renderer. */}
          <img
            src={image}
            alt={`${title}のテーブル配置図`}
            onError={() => setError(true)}
          />
          <span aria-hidden="true">拡大表示</span>
        </button>
      ) : image && !error && href === null ? (
        // biome-ignore lint/performance/noImgElement: Original Cue Layout renderer supplies the authenticated SVG Blob.
        <img
          src={image}
          alt={`${title}のテーブル配置図`}
          onError={() => setError(true)}
        />
      ) : image && !error ? (
        <a
          className="layout-preview-link"
          href={href ?? authoring.href(`/layouts/${id}`)}
          aria-label={`${title}を開く`}
        >
          {/* biome-ignore lint/performance/noImgElement: SVG is a local authenticated Blob, not an optimizable server image. */}
          <img
            src={image}
            alt={`${title}のテーブル配置図`}
            onError={() => setError(true)}
          />
        </a>
      ) : error ? (
        <div className="layout-preview-message">
          <span>プレビューを表示できません</span>
          <button
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
          >
            再読み込み
          </button>
        </div>
      ) : (
        <span className="layout-preview-message" role="status">
          プレビューを読み込み中…
        </span>
      )}
    </div>
  );
}
